import {
  CSSProperties,
  forwardRef,
  Fragment,
  ReactNode,
  RefObject,
} from 'react';
import * as S from './styles';
import Label from '@/components/atoms/Label';
import Text from '@/components/atoms/Text';
import { useMask } from '@react-input/mask';

export type TextFieldSize = 1 | 2;
export type TextFieldVariant = 'surface' | 'soft';
export type TextFieldState =
  | 'placeholder'
  | 'hover'
  | 'active'
  | 'filled'
  | 'disable'
  | 'read-only';

export type TextFieldProps = {
  $size: TextFieldSize;
  $variant: TextFieldVariant;
  $state: TextFieldState;
  $error: string | boolean;
  $ItemStart?: ReactNode;
  $label?: string;
  $helperText?: string;
  $ItemEnd?: ReactNode;
  $focusRing: boolean;
  $placeholder: string;
  $name: string;
  $obrigatorio?: boolean;
  $inputProps?: object;
  $mask?: string;
  $style?: CSSProperties;
  $prevenirFocoInput?: boolean;
};

const TextField = forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
  const {
    $name,
    $size,
    $state,
    $placeholder,
    $variant,
    $label,
    $ItemStart,
    $ItemEnd,
    $error,
    $obrigatorio = true,
    $inputProps,
    $mask,
    $style = undefined,
    $prevenirFocoInput = false,
  } = props;
  const propsContainer = { $size, $state, $variant, $error };

  const maskRef = useMask({
    mask: $mask ?? '',
    replacement: { _: /\d/ },
  });

  function scrollParaInput() {
    const inputRef = ($mask ? maskRef : ref) as RefObject<HTMLInputElement>;

    if (!inputRef?.current) return;
    if ($prevenirFocoInput) return;

    const target = inputRef?.current;
    const rect = target.getBoundingClientRect();

    const viewportHeight = window.visualViewport?.height || window.innerHeight;

    const isVisible = rect.top >= 0 && rect.bottom <= viewportHeight;

    if (!isVisible) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  return (
    <S.ContainerWrapper style={$style}>
      {$label ? (
        <Label
          $size={1}
          $obrigatorio={$obrigatorio}
          $highContrast={false}
          $label={$label}
        />
      ) : null}
      <S.Container {...propsContainer} onClick={scrollParaInput}>
        {$ItemStart ? <Fragment>{$ItemStart}</Fragment> : null}
        <S.Input
          ref={$mask ? maskRef : ref}
          name={$name}
          type="text"
          placeholder={$placeholder}
          {...$inputProps}
        />
        {$ItemEnd ? <Fragment>{$ItemEnd}</Fragment> : null}
      </S.Container>
      {$error && typeof $error === 'string' ? (
        <Text
          $size={1}
          $color="error"
          $weight="bold"
          $align="left"
          $highContrast={true}
          $text={$error}
        />
      ) : null}
    </S.ContainerWrapper>
  );
});

TextField.displayName = 'TextField';

export default TextField;
